import { Component, OnInit, inject } from '@angular/core';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import {
	MollakProxyEnvironment,
	MollakProxyExecutionResult,
	MollakProxyFormValue,
	MollakProxyService,
} from './mollak-proxy.service';

interface ExecutionResultViewModel {
	environment: string;
	status: number;
	payload: unknown;
	isError: boolean;
	correlationId?: string | null;
}

@Component( {
    selector: 'app-mollak-proxy',
    imports: [ReactiveFormsModule, CommonModule],
    templateUrl: './mollak-proxy.component.html',
    styleUrls: ['./mollak-proxy.component.scss'],
    
} )
export class MollakProxyComponent implements OnInit {
	private readonly fb = inject( FormBuilder );
	private readonly mollakProxyService = inject( MollakProxyService );

	isLoading = false;
	jsonError: string | null = null;
	executionResult: ExecutionResultViewModel | null = null;

	readonly proxyForm = this.fb.group( {
		environment: ['Production', [ Validators.required ] ],
		requestMethod: ['GET', [ Validators.required ] ],
		endpointPath: ['', [ Validators.required, Validators.pattern( /^\/.+/ ) ] ],
		requestBody: [{ value: '', disabled: true }],
	} );

	// The template reads the environment through this stream (via the async pipe) instead of
	// a manually mirrored class field, so what's displayed can never drift from what the
	// FormControl actually holds. startWith replays the current value immediately, since
	// valueChanges only emits on subsequent changes.
	readonly environment$: Observable<MollakProxyEnvironment> = this.proxyForm.controls.environment.valueChanges.pipe(
		startWith( this.proxyForm.controls.environment.value ),
		map( ( value ) => ( value ?? 'Production' ) as MollakProxyEnvironment ),
	);

	ngOnInit (): void {
		this.proxyForm.get( 'requestMethod' )?.valueChanges.subscribe( ( method ) => {
			// Belt-and-suspenders: the POST <option> is also marked [disabled] when Production
			// is selected, but this guard reverts POST -> GET regardless of how the value was
			// set, so Production stays read-only even if something bypasses the disabled option.
			if ( method === 'POST' && this.proxyForm.get( 'environment' )?.value === 'Production' ) {
				this.proxyForm.get( 'requestMethod' )?.setValue( 'GET' );
				return;
			}

			if ( method === 'POST' ) {
				this.proxyForm.get( 'requestBody' )?.enable();
			} else {
				this.proxyForm.get( 'requestBody' )?.disable();
				this.proxyForm.get( 'requestBody' )?.setValue( '' );
			}
		} );

		this.proxyForm.get( 'environment' )?.valueChanges.subscribe( ( value ) => {
			if ( value !== 'Production' ) {
				return;
			}
			const requestMethodControl = this.proxyForm.get( 'requestMethod' );
			if ( requestMethodControl?.value === 'POST' ) {
				requestMethodControl.setValue( 'GET' );
			}
		} );
	}

	getEnvironmentHint ( selectedEnvironment: MollakProxyEnvironment ): string {
		return selectedEnvironment === 'Production' ?
			'Production is read-only through this tool: GET only, using Socienta\'s production Mollak account.' :
			'Test uses Socienta\'s configured Mollak test/sandbox account: GET or POST.';
	}

	onSubmit (): void {
		if ( this.proxyForm.invalid ) {
			this.proxyForm.markAllAsTouched();
			return;
		}

		this.executionResult = null;
		this.jsonError = null;

		const payload = this.buildPayload();
		if ( !payload ) {
			return;
		}

		this.isLoading = true;

		this.mollakProxyService.executeProxyCall( payload )
			.subscribe( {
				next: ( response ) => {
					this.executionResult = this.toViewModel( response );
					this.isLoading = false;
				},
				error: ( error: HttpErrorResponse ) => {
					this.executionResult = this.buildErrorResult( error, payload );
					this.isLoading = false;
				},
			} );
	}

	onReset (): void {
		this.proxyForm.reset( {
			environment: 'Production',
			requestMethod: 'GET',
			endpointPath: '',
			requestBody: '',
		} );
		this.proxyForm.get( 'requestBody' )?.disable();
		this.executionResult = null;
		this.jsonError = null;
	}

	get disabledExecute (): boolean {
		return this.proxyForm.invalid || this.isLoading;
	}

	get responsePrettyPayload (): string {
		if ( !this.executionResult ) {
			return '';
		}
		return JSON.stringify( this.executionResult.payload ?? {}, null, 2 );
	}

	private buildPayload (): MollakProxyFormValue | null {
		const method = this.proxyForm.get( 'requestMethod' )?.value as 'GET' | 'POST';
		const endpointPath = ( this.proxyForm.get( 'endpointPath' )?.value ?? '' ).trim();
		const environment = this.proxyForm.get( 'environment' )?.value as MollakProxyEnvironment;
		const requestBodyValue = this.proxyForm.get( 'requestBody' )?.value;

		if ( method === 'POST' && requestBodyValue ) {
			try {
				const parsedBody = JSON.parse( requestBodyValue );
				return {
					method, endpointPath, environment, payload: parsedBody,
				};
			} catch ( error ) {
				this.jsonError = 'Request body must be valid JSON';
				return null;
			}
		}

		return { method, endpointPath, environment };
	}

	private toViewModel ( response: MollakProxyExecutionResult ): ExecutionResultViewModel {
		return {
			environment: response.environment,
			status: response.status,
			payload: response.data ?? response.error ?? {},
			isError: Boolean( response.error ),
			correlationId: response.correlationId,
		};
	}

	private buildErrorResult ( error: HttpErrorResponse, payload: MollakProxyFormValue ): ExecutionResultViewModel {
		const body = ( error.error ?? {} ) as MollakProxyExecutionResult;
		return {
			environment: body?.environment ?? payload.environment,
			status: body?.status ?? error.status ?? 500,
			payload: body?.error ?? error.message,
			isError: true,
			correlationId: error.headers?.get( 'x-correlation-id' ) ?? body?.correlationId,
		};
	}
}
